import React, { useEffect, useRef, useState } from 'react';

interface Address {
  city: string;
  country: string;
  house_number: string;
  postalCode: string;
  state: string;
  street_name: string;
}

interface PayPalModalProps {
  isOpen: boolean;
  onClose: () => void;
  amount: number;
  paypalKey: string,
  currency: string,
  onSuccess: (data: { transactionId: string; payer: any; order: any }) => void;
  billingAddress?: Address;
}

declare global {
  interface Window {
    paypal: any;
  }
}

const PayPalModal: React.FC<PayPalModalProps> = ({
  isOpen,
  onClose,
  amount,
  paypalKey,
  currency,
  onSuccess,
  billingAddress,
}) => {
  const paypalRef = useRef<HTMLDivElement>(null);
  const [paypalLoaded, setPaypalLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const buttonsInstance = useRef<any>(null);

  const countryCodeMap: Record<string, string> = {
    'india': 'IN',
    'united states': 'US',
    'united kingdom': 'GB',
    'canada': 'CA',
    'australia': 'AU',
    'germany': 'DE',
    'france': 'FR',
    'japan': 'JP',
  };

  const getCountryCode = (countryName: string): string => {
    return countryCodeMap[countryName.toLowerCase()] || countryName;
  };

  // Load PayPal script with intent=capture
  useEffect(() => {
    if (!isOpen) return;

    let script: HTMLScriptElement | null = null;
    let timeout: NodeJS.Timeout;

    const loadPaypalScript = (): Promise<void> => {
      return new Promise((resolve, reject) => {
        if (window.paypal) {
          setPaypalLoaded(true);
          resolve();
          return;
        }

        script = document.createElement('script');
        script.src =
          'https://www.paypal.com/sdk/js?client-id=' + paypalKey + '&currency=' + currency + '&intent=capture&components=buttons';
        script.async = true;
        script.onload = () => {
          setPaypalLoaded(true);
          resolve();
        };
        script.onerror = () => reject(new Error('PayPal SDK failed to load.'));
        document.body.appendChild(script);

        timeout = setTimeout(() => {
          reject(new Error('PayPal SDK loading timed out.'));
        }, 10000);
      });
    };

    setLoading(true);
    setError(null);

    loadPaypalScript()
      .catch((err) => {
        console.error('PayPal loading error:', err);
        setError(err.message);
      })
      .finally(() => {
        setLoading(false);
        clearTimeout(timeout);
      });

    return () => {
      if (script && document.body.contains(script)) {
        document.body.removeChild(script);
      }
      clearTimeout(timeout);
      setPaypalLoaded(false);
      
      if (buttonsInstance.current) {
        try {
          buttonsInstance.current.close();
        } catch (e) {
          console.warn('Error cleaning up PayPal buttons:', e);
        }
        buttonsInstance.current = null;
      }
    };
  }, [isOpen]);

  // Render PayPal buttons
  useEffect(() => {
    if (!isOpen || !paypalLoaded || !paypalRef.current) return;

    paypalRef.current.innerHTML = '';

    try {
      buttonsInstance.current = window.paypal.Buttons({
        style: {
          layout: 'vertical',
          color: 'blue',
          shape: 'pill',
          label: 'paypal',
          height: 45,
        },
        createOrder: (data: any, actions: any) => {
          const purchaseUnit: any = {
            amount: {
              value: String(amount),
              currency_code: currency,
            },
          };

          if (billingAddress) {
            purchaseUnit.shipping = {
              name: {
                full_name: `${billingAddress.house_number} ${billingAddress.street_name}`,
              },
            };
          }

          return actions.order.create({
            purchase_units: [purchaseUnit],
            application_context: {
              shipping_preference: billingAddress ? 'NO_SHIPPING' : 'GET_FROM_FILE',
              user_action: 'PAY_NOW',
            },
          });
        },
        onApprove: async (data: any, actions: any) => {
          try {
            const order = await actions.order.capture();
            const transactionId = order?.id;
            onSuccess({
              transactionId,
              payer: order?.payer,
              order,
            });
            onClose();
          } catch (err) {
            console.error('Payment capture error:', err);
            setError('Payment processing failed. Please try again.');
          }
        },
        onError: (err: any) => {
          console.error('PayPal Error:', err);
          setError(err.message || 'Payment initialization failed.');
        },
        onCancel: (data: any) => {
          console.log('Payment cancelled by user');
        },
      });

      buttonsInstance.current.render(paypalRef.current);
    } catch (error) {
      console.error('PayPal button rendering failed:', error);
      setError('Failed to initialize payment buttons.');
    }
  }, [isOpen, paypalLoaded, amount, onSuccess, onClose, billingAddress]);

  if (!isOpen) return null;

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <button onClick={onClose} style={closeButtonStyle}>
          ×
        </button>
        <h2 style={titleStyle}>Pay with PayPal</h2>
        <p style={amountTextStyle}>Total: {currency} {amount.toFixed(2)}</p>
        
        {loading && <div style={loadingStyle}>Loading payment options...</div>}
        {error && (
          <div style={errorStyle}>
            {error}
            <button 
              onClick={() => window.location.reload()} 
              style={retryButtonStyle}
            >
              Try Again
            </button>
          </div>
        )}
        
        <div ref={paypalRef} style={paypalButtonWrapperStyle} />
      </div>
    </div>
  );
};




// Styles (same as your original styles)
const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: '0',
  zIndex: 1000,
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  overflowY: 'auto',
};

const modalStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: '1rem',
  padding: '2rem 1.5rem',
  width: '90%',
  maxWidth: '420px',
  position: 'relative',
  boxShadow: '0 20px 50px rgba(0,0,0,0.2)',
  animation: 'fadeIn 0.3s ease-in-out',
  textAlign: 'center',
};

const closeButtonStyle: React.CSSProperties = {
  position: 'absolute',
  top: '12px',
  right: '16px',
  fontSize: '1.5rem',
  fontWeight: 'bold',
  color: '#888',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
};

const titleStyle: React.CSSProperties = {
  fontSize: '1.8rem',
  fontWeight: 'bold',
  marginBottom: '0.75rem',
  color: '#1f4ed8',
};

const amountTextStyle: React.CSSProperties = {
  fontSize: '1rem',
  color: '#555',
  marginBottom: '1.5rem',
};

const paypalButtonWrapperStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  minHeight: '50px',
};

const loadingStyle: React.CSSProperties = {
  padding: '1rem',
  color: '#666',
};

const errorStyle: React.CSSProperties = {
  color: '#d32f2f',
  padding: '1rem',
  marginBottom: '1rem',
  backgroundColor: '#ffebee',
  borderRadius: '4px',
};

const retryButtonStyle: React.CSSProperties = {
  marginTop: '0.5rem',
  padding: '0.5rem 1rem',
  backgroundColor: '#1f4ed8',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
};

export default PayPalModal;